import { logger } from '../logger.js';

const REFRESH_PHASE_TIMEOUT_MS = 3_000;

/**
 * Resolve `promise`, but never later than `ms`; on timeout resolve `fallbackValue`
 * instead. A rejection also resolves `fallbackValue` so a failed phase can never
 * block the refresh. Keeps every phase of `forceRefresh` bounded.
 */
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  fallbackValue: T,
): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(fallbackValue);
      }
    }, ms);
    const finish = (value: T): void => {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        resolve(value);
      }
    };
    void promise.then(
      (value) => finish(value),
      () => finish(fallbackValue),
    );
  });
}

/**
 * Make the newest available service worker take control of this page.
 *
 * This is the Safari/WKWebView fix. `registration.unregister()` does NOT detach
 * the worker that already controls the live page — it only stops FUTURE
 * navigations from being controlled. On WebKit the immediately-following
 * `location.replace()` is still handled by the outgoing worker, whose
 * `navigateFallback:'/index.html'` precache route returns the OLD shell (the
 * `?v=` buster is a query string the navigation route ignores, and even with
 * caches purged the outgoing worker re-fetches its old revisioned `/index.html`
 * from the HTTP cache). So instead of trying to get rid of the old worker, we
 * bring the NEW one to the front: pull the freshly deployed `sw.js`
 * (`registration.update()`), nudge a worker that is waiting to activate
 * (`SKIP_WAITING`, honored by the vite-plugin-pwa worker), and wait for the
 * `controllerchange` that fires when it claims the page. After that the next
 * navigation is served by the new worker's precache — the new bundle.
 *
 * Returns `true` once the new worker controls the page, `false` when there is
 * nothing newer to activate (no worker, or already the newest), or when the
 * caller aborts `signal` — so a timed-out takeover detaches its listeners and
 * stops nudging instead of racing the caller's teardown.
 */
async function activateNewestWorker(signal: AbortSignal): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
    return false;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration || signal.aborted) return false;
  await registration.update().catch(() => undefined);
  const pendingWorker = registration.waiting ?? registration.installing;
  /**
   * An already-`redundant` candidate will never claim the page — no further
   * `statechange`, no `controllerchange` — so fail fast rather than hang until
   * the caller's timeout, letting the caller run the teardown fallback now.
   */
  if (!pendingWorker || pendingWorker.state === 'redundant' || signal.aborted)
    return false;
  /**
   * Narrowed non-null binding so the closures below keep the type — TS widens a captured `const`
   * back to `ServiceWorker | null` inside nested functions.
   */
  const worker = pendingWorker;
  return new Promise<boolean>((resolve) => {
    /**
     * Detach every listener so a takeover the caller has given up on (its 3s
     * cap fired → `signal` aborted) can no longer post `SKIP_WAITING` or resolve
     * while teardown is already unregistering workers.
     */
    function finish(claimed: boolean): void {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      worker.removeEventListener('statechange', onStateChange);
      signal.removeEventListener('abort', onAbort);
      resolve(claimed);
    }
    /**
     * `SKIP_WAITING` only reaches a worker once it is `installed`. A worker still
     * `installing` right after `update()` has no `registration.waiting` handle
     * yet, so nudge it on its install `statechange` (and now if already
     * installed/waiting); never once the takeover is aborted.
     */
    function skipWaiting(): void {
      if (!signal.aborted && worker.state === 'installed') {
        worker.postMessage({ type: 'SKIP_WAITING' });
      }
    }
    const onControllerChange = (): void => finish(true);
    const onStateChange = (): void => {
      skipWaiting();
      /** A failed install (`redundant`) never claims the page — stop waiting. */
      if (worker.state === 'redundant') finish(false);
    };
    const onAbort = (): void => finish(false);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
    );
    worker.addEventListener('statechange', onStateChange);
    signal.addEventListener('abort', onAbort, { once: true });
    skipWaiting();
  });
}

/**
 * Unregister every service worker and drop all CacheStorage entries. The two touch
 * independent browser stores, so they run in parallel to shorten the pre-navigation stall.
 */
async function tearDownServiceWorkerAndCaches(): Promise<void> {
  const unregisterServiceWorkers =
    'serviceWorker' in navigator
      ? navigator.serviceWorker
          .getRegistrations()
          .then((registrations) =>
            Promise.all(
              registrations.map((registration) => registration.unregister()),
            ),
          )
      : Promise.resolve([]);
  const purgeCaches =
    'caches' in window
      ? caches
          .keys()
          .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      : Promise.resolve([]);
  await Promise.all([unregisterServiceWorkers, purgeCaches]);
}

/** Current location rebuilt with a `v` cache-buster so the navigation bypasses HTTP caches. */
export function buildCacheBustedUrl(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  now: number,
): string {
  const params = new URLSearchParams(location.search);
  params.set('v', String(now));
  return `${location.pathname}?${params.toString()}${location.hash}`;
}

/**
 * Land the newest bundle on a user-initiated refresh, then leave via a plain navigation.
 *
 * First try to bring the newest service worker to the front (`activateNewestWorker`):
 * on Safari/WKWebView the old worker keeps controlling the live page across an
 * `unregister()`, so the reliable move is to make the NEW worker claim the page
 * before we navigate — see microsoft-teams-library-js#1205 and teams-toolkit#11913.
 * The teardown fallback (unregister every worker + purge every cache, so the
 * navigation hits the network for a fresh shell) runs in every case EXCEPT a
 * confirmed claim (`'claimed'`): both a definitive "nothing newer" (`'none'`)
 * and a takeover still unfinished at the cap (`'timeout'` — which also covers a
 * hung `getRegistration()`/`update()` where no `SKIP_WAITING` ever ran) fall
 * back to teardown. Only a fired `controllerchange` resolves `'claimed'` before
 * the cap, so skipping teardown there cannot undo a real claim.
 *
 * The exit is `location.replace()` with a `v` cache-buster: a regular navigation
 * the webview always executes (it swallows `location.reload()` from a tab
 * iframe) which also bypasses the HTTP cache. Each phase is capped at 3s so a
 * hung update check or `caches.delete()` can never block the navigation. Runs
 * only when the user asks for it — there is no automatic reload.
 */
export async function forceRefresh(
  navigate: (url: string) => void = (url) => window.location.replace(url),
): Promise<void> {
  const takeoverAbort = new AbortController();
  const takeover = await withTimeout<'claimed' | 'none' | 'timeout'>(
    activateNewestWorker(takeoverAbort.signal).then(
      (claimed) => (claimed ? 'claimed' : 'none'),
      (err: unknown) => {
        logger.warn(
          'forceRefresh worker takeover failed; falling back to teardown',
          err,
        );
        return 'none';
      },
    ),
    REFRESH_PHASE_TIMEOUT_MS,
    'timeout',
  );
  if (takeover !== 'claimed') {
    /**
     * Cancel a takeover still in flight at the cap so it detaches its listeners
     * and stops nudging before teardown starts unregistering workers.
     */
    takeoverAbort.abort();
    await withTimeout(
      tearDownServiceWorkerAndCaches().catch((err: unknown) => {
        logger.warn('forceRefresh teardown failed; navigating anyway', err);
      }),
      REFRESH_PHASE_TIMEOUT_MS,
      undefined,
    );
  }
  navigate(buildCacheBustedUrl(window.location, Date.now()));
}
